import { Head, Link } from "@inertiajs/react"
import { useTranslation } from "react-i18next"

import { AnalyticsCollabFunnelChart } from "@/components/brand/analytics/analytics-collab-funnel-chart"
import { AnalyticsCreatorsTable } from "@/components/brand/analytics/analytics-creators-table"
import { AnalyticsDetailKpiStrip } from "@/components/brand/analytics/analytics-detail-kpi-strip"
import { AnalyticsErrorState } from "@/components/brand/analytics/analytics-error-state"
import AppLayout from "@/layouts/app-layout"
import { brandAnalyticsMissionsPath, missionPath } from "@/routes"
import type { MissionAnalyticsDetailProps } from "@/types/mission-analytics"

/**
 * OKS-63 — Per-mission analytics deep-dive
 * (`/brand/missions/:id/analytics`).
 *
 * # State branch (TRD §4)
 *
 *   1. `error === true` → `<AnalyticsErrorState>` with Retry.
 *   2. Draft mission → render the draft banner above the populated
 *      sections.
 *   3. populated → KPI strip + funnel chart + creators table.
 */
export default function BrandAnalyticsMissionsShow({
  mission,
  kpis,
  funnel,
  creators_page: creatorsPage,
  error,
}: MissionAnalyticsDetailProps) {
  const { t } = useTranslation()
  const isDraft = mission.status === "draft"

  return (
    <AppLayout>
      <Head title={`${mission.title} — ${t("analytics.head_title")}`} />
      <div
        className="flex flex-col gap-6 p-4 md:p-6"
        data-testid="analytics-detail-page"
      >
        <header className="flex flex-col gap-2">
          <Link
            href={brandAnalyticsMissionsPath()}
            className="text-primary text-sm font-medium hover:underline"
            data-testid="analytics-detail-back-link"
          >
            {t("analytics.detail.back_link")}
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h1 className="text-2xl font-semibold tracking-tight">
              {mission.title}
            </h1>
            <Link
              href={missionPath(mission.id)}
              className="text-primary text-sm font-medium hover:underline"
              data-testid="analytics-detail-view-mission"
            >
              {t("analytics.detail.view_mission")}
            </Link>
          </div>
        </header>

        {error ? (
          <AnalyticsErrorState
            reloadOnly={["mission", "kpis", "funnel", "creators_page"]}
          />
        ) : (
          <>
            {isDraft ? (
              <p
                data-testid="analytics-detail-draft-banner"
                className="bg-muted text-muted-foreground rounded-md border p-3 text-sm"
              >
                {t("analytics.detail.draft_banner")}
              </p>
            ) : null}

            {kpis ? <AnalyticsDetailKpiStrip kpis={kpis} /> : null}
            {funnel ? (
              <AnalyticsCollabFunnelChart
                funnel={funnel}
                missionId={mission.id}
                missionTitle={mission.title}
              />
            ) : null}
            {creatorsPage ? (
              <AnalyticsCreatorsTable
                creatorsPage={creatorsPage}
                missionId={mission.id}
              />
            ) : null}
          </>
        )}
      </div>
    </AppLayout>
  )
}
